import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Box,
  Collapse,
  Tooltip,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  Dashboard,
  Business,
  People,
  Groups,
  PersonAdd,
  Inventory,
  Policy,
  AttachMoney,
  AccountBalance,
  SupportAgent,
  Description,
  Assessment,
  ExpandLess,
  ExpandMore,
  Receipt,
  Payment,
  TrendingUp,
  AccountBalanceWallet,
  Autorenew,
  ConfirmationNumber,
  Today,
  CallReceived,
  CallMade,
  BarChart,
  ManageAccounts,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  drawerWidth: number;
  drawerWidthCollapsed: number;
  collapsed: boolean;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path?: string;
  allowedRoles?: UserRole[];
  children?: MenuItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, drawerWidth, drawerWidthCollapsed, collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [financeOpen, setFinanceOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);

  // Flyout submenu state (collapsed mode only)
  const [flyoutAnchorEl, setFlyoutAnchorEl] = useState<null | HTMLElement>(null);
  const [flyoutMenu, setFlyoutMenu] = useState<MenuItem | null>(null);

  const openFlyout = (event: React.MouseEvent<HTMLElement>, item: MenuItem) => {
    setFlyoutAnchorEl(event.currentTarget);
    setFlyoutMenu(item);
  };

  const closeFlyout = () => {
    setFlyoutAnchorEl(null);
    setFlyoutMenu(null);
  };

  const menuItems: MenuItem[] = [
    {
      text: 'แดชบอร์ด',
      icon: <Dashboard />,
      path: '/dashboard',
    },
    {
      text: 'บริษัทประกันภัย',
      icon: <Business />,
      path: '/companies',
      allowedRoles: [UserRole.ADMIN, UserRole.MANAGER],
    },
    {
      text: 'ตัวแทนประกันภัย',
      icon: <People />,
      path: '/agents',
      allowedRoles: [UserRole.ADMIN, UserRole.MANAGER],
    },
    {
      text: 'ทีมงาน',
      icon: <Groups />,
      path: '/teams',
      allowedRoles: [UserRole.ADMIN, UserRole.MANAGER],
    },
    {
      text: 'ลูกค้า',
      icon: <PersonAdd />,
      path: '/customers',
    },
    {
      text: 'ผลิตภัณฑ์ประกันภัย',
      icon: <Inventory />,
      path: '/products',
    },
    {
      text: 'กรมธรรม์',
      icon: <Policy />,
      path: '/policies',
    },
    {
      text: 'ค่าคอมมิชชั่น',
      icon: <AttachMoney />,
      path: '/commissions',
      allowedRoles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.FINANCE, UserRole.AGENT],
    },
    {
      text: 'การเงิน',
      icon: <AccountBalance />,
      allowedRoles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.FINANCE],
      children: [
        { text: 'รายงานการเงินประจำวัน', icon: <Today />, path: '/finance/daily-report' },
        { text: 'เจ้าหนี้การค้า', icon: <Payment />, path: '/finance/accounts-payable' },
        { text: 'ลูกหนี้การค้า', icon: <Receipt />, path: '/finance/accounts-receivable' },
        { text: 'ผลประโยชน์รอรับ', icon: <CallReceived />, path: '/finance/benefits-receivable' },
        { text: 'ผลประโยชน์รอจ่าย', icon: <CallMade />, path: '/finance/benefits-payable' },
        { text: 'รายงานกำไร/ขาดทุน', icon: <BarChart />, path: '/finance/profit-loss' },
        { text: 'กระแสเงินสด', icon: <TrendingUp />, path: '/finance/cash-flow' },
        { text: 'รายการเงิน', icon: <AccountBalanceWallet />, path: '/finance/transactions' },
      ],
    },
    {
      text: 'บริการหลังการขาย',
      icon: <SupportAgent />,
      allowedRoles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.CUSTOMER_SERVICE],
      children: [
        { text: 'การต่ออายุ', icon: <Autorenew />, path: '/services/renewals' },
        { text: 'Ticket บริการ', icon: <ConfirmationNumber />, path: '/services/tickets' },
      ],
    },
    {
      text: 'ใบเสนอราคา',
      icon: <Description />,
      path: '/quotations',
    },
    {
      text: 'รายงาน',
      icon: <Assessment />,
      allowedRoles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.FINANCE],
      children: [
        { text: 'รายงานยอดขาย', icon: <TrendingUp />, path: '/reports/sales' },
        { text: 'รายงานการเงิน', icon: <AccountBalance />, path: '/reports/finance' },
        { text: 'รายงาน คปภ.', icon: <Description />, path: '/reports/oic' },
        { text: 'รายงานผู้บริหาร', icon: <Dashboard />, path: '/reports/management' },
      ],
    },
  ];

  const adminItems: MenuItem[] = [
    {
      text: 'ผู้ใช้งาน',
      icon: <ManageAccounts />,
      path: '/users',
      allowedRoles: [UserRole.ADMIN],
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
    closeFlyout();
  };

  const isMenuItemAllowed = (item: MenuItem): boolean => {
    if (!item.allowedRoles) return true;
    if (!user) return false;
    return item.allowedRoles.includes(user.role);
  };

  // ── Collapsed mode: parent with children → flyout submenu ──────────────
  const renderCollapsedParent = (item: MenuItem) => {
    const hasActiveChild = item.children!.some(
      (child) => child.path && location.pathname.startsWith(child.path)
    );
    const isOpen = flyoutMenu?.text === item.text && Boolean(flyoutAnchorEl);

    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding>
          <Tooltip
            title={item.text}
            placement="right"
            arrow
            disableHoverListener={isOpen}
          >
            <ListItemButton
              selected={hasActiveChild}
              onClick={(e) => openFlyout(e, item)}
              sx={{
                justifyContent: 'center',
                px: 0,
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': { backgroundColor: 'primary.light' },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                  color: hasActiveChild ? 'primary.main' : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </React.Fragment>
    );
  };

  // ── Collapsed mode: leaf item ───────────────────────────────────────────
  const renderCollapsedLeaf = (item: MenuItem) => (
    <ListItem key={item.path} disablePadding>
      <Tooltip title={item.text} placement="right" arrow>
        <ListItemButton
          selected={location.pathname === item.path}
          onClick={() => item.path && handleNavigation(item.path)}
          sx={{
            justifyContent: 'center',
            px: 0,
            '&.Mui-selected': {
              backgroundColor: 'primary.light',
              '&:hover': { backgroundColor: 'primary.light' },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              justifyContent: 'center',
              color: location.pathname === item.path ? 'primary.main' : 'inherit',
            }}
          >
            {item.icon}
          </ListItemIcon>
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );

  // ── Expanded mode: parent with children ────────────────────────────────
  const renderExpandedParent = (item: MenuItem) => {
    const isFinanceMenu = item.text === 'การเงิน';
    const isServiceMenu = item.text === 'บริการหลังการขาย';
    const isReportsMenu = item.text === 'รายงาน';
    const isExpanded = isFinanceMenu ? financeOpen : isServiceMenu ? serviceOpen : isReportsMenu ? reportsOpen : false;
    const hasActiveChild = item.children!.some(
      (child) => child.path && location.pathname.startsWith(child.path)
    );
    const toggle = () => {
      if (isFinanceMenu) setFinanceOpen(!financeOpen);
      if (isServiceMenu) setServiceOpen(!serviceOpen);
      if (isReportsMenu) setReportsOpen(!reportsOpen);
    };

    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={toggle}
            selected={hasActiveChild}
            sx={{
              pl: 1,
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                '&:hover': { backgroundColor: 'primary.light' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: hasActiveChild ? 'primary.main' : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children!.map((child) => (
              <ListItem key={child.path} disablePadding>
                <ListItemButton
                  sx={{ pl: 2 }}
                  selected={location.pathname === child.path}
                  onClick={() => child.path && handleNavigation(child.path)}
                >
                  <ListItemIcon
                    sx={{ minWidth: 40, color: location.pathname === child.path ? 'primary.main' : 'inherit' }}
                  >
                    {child.icon}
                  </ListItemIcon>
                  <ListItemText primary={child.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  // ── Expanded mode: leaf item ───────────────────────────────────────────
  const renderExpandedLeaf = (item: MenuItem) => (
    <ListItem key={item.path} disablePadding>
      <ListItemButton
        selected={location.pathname === item.path}
        onClick={() => item.path && handleNavigation(item.path)}
        sx={{
          pl: 1,
          '&.Mui-selected': {
            backgroundColor: 'primary.light',
            '&:hover': { backgroundColor: 'primary.light' },
          },
        }}
      >
        <ListItemIcon
          sx={{ minWidth: 40, color: location.pathname === item.path ? 'primary.main' : 'inherit' }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItemButton>
    </ListItem>
  );

  const renderMenuItem = (item: MenuItem) => {
    if (item.children) {
      return collapsed ? renderCollapsedParent(item) : renderExpandedParent(item);
    }
    return collapsed ? renderCollapsedLeaf(item) : renderExpandedLeaf(item);
  };

  const desktopWidth = collapsed ? drawerWidthCollapsed : drawerWidth;

  const allowedAdminItems = adminItems.filter(isMenuItemAllowed);

  const drawerContent = (
    <Box sx={{ backgroundColor: '#fff', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar />
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <List disablePadding sx={{ py: 0 }}>
          {menuItems.filter(isMenuItemAllowed).map(renderMenuItem)}
        </List>
      </Box>
      {allowedAdminItems.length > 0 && (
        <>
          <Divider />
          <List disablePadding sx={{ py: 0 }}>
            {allowedAdminItems.map(renderMenuItem)}
          </List>
        </>
      )}
    </Box>
  );

  // Full-labels content for mobile (never collapsed)
  const mobileDrawerContent = (
    <Box sx={{ backgroundColor: '#fff', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar />
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <List disablePadding sx={{ py: 0 }}>
          {menuItems.filter(isMenuItemAllowed).map((item) => {
            if (item.children) return renderExpandedParent(item);
            return renderExpandedLeaf(item);
          })}
        </List>
      </Box>
      {allowedAdminItems.length > 0 && (
        <>
          <Divider />
          <List disablePadding sx={{ py: 0 }}>
            {allowedAdminItems.map((item) => renderExpandedLeaf(item))}
          </List>
        </>
      )}
    </Box>
  );

  return (
    <>
      {/* Flyout submenu for collapsed desktop mode */}
      <Menu
        anchorEl={flyoutAnchorEl}
        open={Boolean(flyoutAnchorEl) && flyoutMenu !== null}
        onClose={closeFlyout}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ ml: '4px' }}
        MenuListProps={{ disablePadding: true }}
      >
        {flyoutMenu && (
          <>
            <Box sx={{ px: 2, py: 1, bgcolor: 'primary.main' }}>
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                {flyoutMenu.text}
              </Typography>
            </Box>
            <Divider />
            {flyoutMenu.children?.map((child) => (
              <MenuItem
                key={child.path}
                selected={location.pathname === child.path}
                onClick={() => child.path && handleNavigation(child.path)}
                sx={{
                  gap: 1.5,
                  py: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': { backgroundColor: 'primary.light' },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    color: location.pathname === child.path ? 'primary.main' : 'inherit',
                  }}
                >
                  {child.icon}
                </ListItemIcon>
                <Typography variant="body2">{child.text}</Typography>
              </MenuItem>
            ))}
          </>
        )}
      </Menu>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            zIndex: (theme) => theme.zIndex.drawer,
          },
        }}
      >
        {mobileDrawerContent}
      </Drawer>

      {/* Desktop drawer — collapsible */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: desktopWidth,
          flexShrink: 0,
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: desktopWidth,
            position: 'fixed',
            overflowX: 'hidden',
            overflowY: 'auto',
            zIndex: (theme) => theme.zIndex.drawer,
            backgroundColor: '#fff',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
